import React, { useState } from 'react';
import { BarChart2, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { useActivityLogStore } from '../../../stores/activityLogStore';
import { useUserStore } from '../../../stores/userStore';
import UserStatsChart from './stats/UserStatsChart';
import UserList from './stats/UserList';
import { TRANSITIONS } from '../../../utils/animations';

export default function UserStats() {
  const [view, setView] = useState<'overview' | 'list'>('overview');
  const logs = useActivityLogStore((state) => state.logs);
  const currentUser = useUserStore((state) => state.currentUser);

  // Calculate statistics
  const totalUsers = currentUser ? 1 : 0;
  const activeUsers = new Set(logs.filter(log => 
    new Date(log.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  ).map(log => log.userId)).size;

  const newUsersThisWeek = logs.filter(log => 
    log.action === 'signup' && 
    new Date(log.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  ).length;

  const userGrowth = ((newUsersThisWeek / totalUsers) * 100) || 0;

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`bg-white p-6 rounded-lg border border-gray-100 ${TRANSITIONS.CARD}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">{totalUsers}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={`bg-white p-6 rounded-lg border border-gray-100 ${TRANSITIONS.CARD}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Users (7d)</p>
              <p className="text-2xl font-semibold text-gray-900">{activeUsers}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <BarChart2 className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className={`bg-white p-6 rounded-lg border border-gray-100 ${TRANSITIONS.CARD}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">User Growth</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-semibold text-gray-900">{userGrowth.toFixed(1)}%</p>
                {userGrowth > 0 ? (
                  <ArrowUp className="h-5 w-5 text-green-500" />
                ) : (
                  <ArrowDown className="h-5 w-5 text-red-500" />
                )}
              </div>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <ArrowUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex space-x-4">
        <button
          onClick={() => setView('overview')}
          className={`px-4 py-2 rounded-lg ${
            view === 'overview'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${TRANSITIONS.DEFAULT}`}
        >
          Overview
        </button>
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-lg ${
            view === 'list'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${TRANSITIONS.DEFAULT}`}
        >
          User List
        </button>
      </div>

      {/* Content */}
      {view === 'overview' ? (
        <UserStatsChart logs={logs} />
      ) : (
        <UserList />
      )}
    </div>
  );
}